import React, { createContext, useContext, useState, useEffect, ReactNode, useRef } from 'react';
import axios from 'axios';
import { API_CONFIG, API_TIMEOUT_MS } from '../config/api';

interface User {
  id: number;
  email: string;
  role: string;
  tenant: {
    id: number;
    name: string;
  } | null;
}

interface AuthContextType {
  user: User | null;
  isAuthenticated: boolean;
  loading: boolean;
  error: string | null;
  login: (email: string, password: string) => Promise<{ success: boolean; user?: User; error?: string }>;
  supportLogin: (email: string, password: string) => Promise<boolean>;
  logout: () => void;
  checkAuth: () => void;
  updateUser: (userData: Partial<User>) => void;
  updateTenant: (tenantData: Partial<User['tenant']>) => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const checkAuthRef = useRef(false);
  const retryCountRef = useRef(0);

  // Set up axios defaults with proper API URL
  axios.defaults.baseURL = API_CONFIG.FULL_BASE_URL;
  axios.defaults.timeout = API_TIMEOUT_MS;

  // Add request interceptor to include token
  useEffect(() => {
    const requestInterceptor = axios.interceptors.request.use(
      (config) => {
        const token = localStorage.getItem('token');
        if (token) {
          config.headers.Authorization = `Bearer ${token}`;
        }
        return config;
      },
      (error) => {
        return Promise.reject(error);
      }
    );

    return () => {
      axios.interceptors.request.eject(requestInterceptor);
    };
  }, []);

  // Add response interceptor to handle auth errors conservatively
  useEffect(() => {
    const responseInterceptor = axios.interceptors.response.use(
      (response) => response,
      (error) => {
        // Do not nuke the token for arbitrary 401s during bootstrap; let /auth/me decide
        return Promise.reject(error);
      }
    );

    return () => {
      axios.interceptors.response.eject(responseInterceptor);
    };
  }, []);

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (token) {
      if (!checkAuthRef.current) {
        checkAuthRef.current = true;
        checkAuth();
      }
    } else {
      setUser(null);
      setLoading(false);
    }

    // Cleanup function to reset ref when component unmounts
    return () => {
      // Don't reset checkAuthRef here as it might interfere with re-authentication
    };
  }, []);

  const checkAuth = async () => {
    const token = localStorage.getItem('token');
    if (!token) {
      setUser(null);
      setLoading(false);
      return;
    }

    try {
      setError(null);
      
      // Add a timeout to prevent hanging
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), API_TIMEOUT_MS);
      
      const response = await axios.get('/auth/me', {
        signal: controller.signal
      });
      
      clearTimeout(timeoutId);
      
      if (response.data.success) {
        setUser(response.data.data.user);
        retryCountRef.current = 0; // Reset retry count on success
      } else {
        localStorage.removeItem('token');
        setUser(null);
      }
    } catch (error: any) {
      if (error.name === 'AbortError') {
        console.warn('Auth check timed out, keeping token for now');
        // Don't clear token on timeout, just set loading to false
      } else {
        const status = error?.response?.status;
        if (status === 401) {
          // Clear token on authentication failures
          localStorage.removeItem('token');
          setUser(null);
          console.log('Token invalid or expired, cleared from storage');
        } else {
          // For network errors or other issues, don't clear the token
          // but still set loading to false so the app can function
          console.warn('Auth check failed but keeping token:', error?.response?.data?.error || 'Network error');
          
          // Retry once for network errors
          if (retryCountRef.current < 1) {
            retryCountRef.current++;
            console.log('Retrying auth check...');
            setTimeout(() => checkAuth(), 2000); // Retry after 2 seconds
            return; // Don't set loading to false yet
          }
        }
      }
    } finally {
      setLoading(false);
    }
  };

  const login = async (email: string, password: string): Promise<{ success: boolean; user?: User; error?: string }> => {
    try {
      setError(null);
      setLoading(true);

      const response = await axios.post('/auth/login', { email, password });

      if (response.data.success) {
        localStorage.setItem('token', response.data.data.token);
        setUser(response.data.data.user);
        checkAuthRef.current = true; // Set ref to true since we're now authenticated
        retryCountRef.current = 0; // Reset retry count
        return { success: true, user: response.data.data.user };
      } else {
        const errorMessage = response.data.error || 'Login failed';
        return { success: false, error: errorMessage };
      }
    } catch (error: any) {
      const errorMessage = error.response?.data?.error || 'Login failed';
      return { success: false, error: errorMessage };
    } finally {
      setLoading(false);
    }
  };

  const supportLogin = async (email: string, password: string): Promise<boolean> => {
    try {
      setError(null);
      setLoading(true);

      const response = await axios.post('/support/login', { email, password });

      if (response.data.success) {
        localStorage.setItem('token', response.data.data.token);
        setUser(response.data.data.user);
        checkAuthRef.current = true; // Set ref to true since we're now authenticated
        retryCountRef.current = 0; // Reset retry count
        return true;
      } else {
        setError(response.data.error || 'Support login failed');
        return false;
      }
    } catch (error: any) {
      setError(error.response?.data?.error || 'Support login failed');
      return false;
    } finally {
      setLoading(false);
    }
  };

  const logout = async () => {
    try {
      await axios.delete('/auth/logout');
    } catch (error) {
      // ignore
    } finally {
      localStorage.removeItem('token');
      setUser(null);
      checkAuthRef.current = false; // Reset the ref so future auth checks work
      retryCountRef.current = 0; // Reset retry count
    }
  };

  const updateUser = (userData: Partial<User>) => {
    if (user) {
      setUser({ ...user, ...userData });
    }
  };

  const updateTenant = (tenantData: Partial<User['tenant']>) => {
    if (user && user.tenant) {
      setUser({
        ...user,
        tenant: { ...user.tenant, ...tenantData }
      });
    }
  };

  const value: AuthContextType = {
    user,
    isAuthenticated: !!user,
    loading,
    error,
    login,
    supportLogin,
    logout,
    checkAuth,
    updateUser,
    updateTenant,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
